<template>
  <b-modal
    size="lg"
    title="Asset Detail"
    hide-footer
    v-model="modalValue"
    body-class="p-0"
  >
    <div v-if="detailAsset && detailAsset.massId">
      <div class="bg-light p-3 d-flex align-items-start border-bottom">
        <img
          :src="detailAsset.massImage !== '' ? '/api/' + detailAsset.massImage : 'https://placehold.co/120x120?text=No+Image'"
          class="rounded mr-3 shadow-sm"
          style="width: 100px; height: 100px; object-fit: cover;"
        />
        <div class="flex-grow-1">
          <h5 class="font-weight-bold mb-1">{{ detailAsset.massName }}</h5>
          <div class="mb-2">
            <b-badge variant="primary" class="mr-1">{{ detailAsset.massSystemType }}</b-badge>
            <b-badge variant="info">{{ detailAsset.massMaty ? detailAsset.massMaty.matyName : 'Unknown Type' }}</b-badge>
          </div>
          <div class="text-muted small">
            <i class="ri-calendar-line mr-1"></i> Installation Date:
            <span class="text-dark font-weight-bold">{{ detailAsset.massInstallationDate || '-' }}</span>
          </div>
        </div>
      </div>

      <div class="p-3">
        <b-row>
          <b-col md="6" class="border-right border-md-0">
            <h6 class="text-uppercase text-secondary mb-3 small font-weight-bold border-bottom pb-1">
              <i class="ri-user-star-line mr-1"></i> Contact Person (PIC)
            </h6>
            <div class="detail-row mb-2">
              <span class="text-muted small">Name</span>
              <span class="font-weight-bold d-block">{{ detailAsset.massPicName || '-' }}</span>
            </div>
            <div class="detail-row mb-2">
              <span class="text-muted small">Contact / Phone</span>
              <span class="font-weight-bold d-block">{{ detailAsset.massPicContact || '-' }}</span>
            </div>
            <div class="detail-row mb-2">
              <span class="text-muted small">Email</span>
              <span class="font-weight-bold d-block">{{ detailAsset.massPicEmail || '-' }}</span>
            </div>
            <div class="detail-row mb-2">
              <span class="text-muted small">Email CC</span>
              <span class="font-weight-bold d-block">{{ detailAsset.massPicEmailCc || '-' }}</span>
            </div>
          </b-col>

          <b-col md="6">
            <h6 class="text-uppercase text-secondary mb-3 small font-weight-bold border-bottom pb-1">
              <i class="ri-wifi-line mr-1"></i> Connectivity & Hardware
            </h6>
            <b-row>
              <b-col cols="6" class="mb-2">
                <span class="text-muted small d-block">GSM Number</span>
                <span class="font-weight-bold">{{ detailAsset.massGsmNo || '-' }}</span>
              </b-col>
              <b-col cols="6" class="mb-2">
                <span class="text-muted small d-block">IMEI</span>
                <span class="font-weight-bold">{{ detailAsset.massImei || '-' }}</span>
              </b-col>
              <b-col cols="6" class="mb-2">
                <span class="text-muted small d-block">Telco Operator</span>
                <span class="font-weight-bold">{{ detailAsset.massTelcoOperator || '-' }}</span>
              </b-col>
              <b-col cols="6" class="mb-2">
                <span class="text-muted small d-block">Satellite ID</span>
                <span class="font-weight-bold">{{ detailAsset.massSateliteId || '-' }}</span>
              </b-col>
              <b-col cols="6" class="mb-2">
                <span class="text-muted small d-block">Sat. Operator</span>
                <span class="font-weight-bold">{{ detailAsset.massSateliteOperator || '-' }}</span>
              </b-col>
              <b-col cols="6" class="mb-2">
                <span class="text-muted small d-block">Sat. Device ID</span>
                <span class="font-weight-bold">{{ detailAsset.massSatDeviceId || '-' }}</span>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </div>

      <div class="p-3 border-top bg-white">
        <h6 class="text-uppercase text-secondary mb-3 small font-weight-bold">
          <i class="ri-sensor-line mr-1"></i> Connected Devices
        </h6>
        <div v-if="detailDevices && detailDevices.length > 0" class="table-responsive">
          <table class="table table-sm table-borderless table-striped small mb-0">
            <thead class="bg-light">
              <tr>
                <th class="pl-3">Name</th>
                <th>Type</th>
                <th>Unique ID</th>
                <th>Status</th>
                <th class="text-right pr-3">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="dev in detailDevices" :key="dev.devcId">
                <td class="font-weight-bold pl-3">{{ dev.devcName }}</td>
                <td>
                  <b-badge variant="soft-info" class="text-uppercase">{{ dev.devcType }}</b-badge>
                </td>
                <td class="text-muted font-italic">{{ dev.devcUniqueId }}</td>
                <td>
                  <b-badge :variant="dev.devcStatus === 1 ? 'success' : 'secondary'">
                    {{ dev.devcStatus === 1 ? 'Active' : 'Inactive' }}
                  </b-badge>
                </td>
                <td class="text-right pr-3">
                  <b-button
                    variant="light"
                    size="sm"
                    class="rounded-circle text-primary p-0 d-flex align-items-center justify-content-center border-0"
                    style="width: 24px; height: 24px;"
                    @click="openDeviceDetail(dev)"
                    v-b-tooltip.hover
                    title="View Device Data"
                  >
                    <i class="ri-eye-line"></i>
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="text-muted small text-center py-2 bg-light rounded">
          No devices linked to this asset.
        </div>
      </div>

      <div class="bg-lighter p-2 px-3 border-top d-flex justify-content-between small text-muted">
        <div>
          Created: <b>{{ detailAsset.massCreatedUserId }}</b> ({{ detailAsset.massCreatedTime }})
        </div>
        <div>
          Updated: <b>{{ detailAsset.massUpdatedUserId }}</b> ({{ detailAsset.massUpdatedTime }})
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'AssetDetailModal',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    detailAsset: {
      type: Object,
      default: null,
    },
    detailDevices: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    modalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
  },
  methods: {
    openDeviceDetail(data) {
      this.$router.push({ name: 'DeviceDetail', params: { id: data.devcId } });
    },
  },
};
</script>
